// ONE frontend module access layer (Phase 2). Consumes the backend-resolved tenant module map (GET /business
// `features` + `module_history`, produced by the authoritative backend Module Registry). No resolution logic
// lives here: core/optional/grandfather rules are decided server-side. Frontend gating is UX only; the backend
// enforces every module and permission. Role/permission checks stay separate (see ModuleGate / callers).
import { useMemo } from "react";
import { useAuth } from "@/context/AuthContext";

const warned = new Set();
function warnUnknown(key) {
  if (warned.has(key)) return;
  warned.add(key);
  if (process.env.NODE_ENV !== "production") console.warn(`[modules] unknown module key "${key}" — not in the backend Module Registry; treated as OFF`);
}

// Pure builder (unit-testable). `map` = resolved {key: bool} or null while the business profile is loading.
export function buildModules(business, loading = false) {
  const map = business?.features && typeof business.features === "object" ? business.features : null;
  const history = business?.module_history || {};
  const ready = !!map && !loading;
  const resolved = ready && Object.keys(map).length > 0; // an empty map (bare offline snapshot) is not a resolved registry
  const known = (key) => {
    if (!map) return false;
    if (key in map) return true;
    if (resolved) warnUnknown(key);
    return false; // fail closed: unknown optional module UI stays hidden
  };
  const isEnabled = (key) => known(key) && map[key] === true;
  const hasHistory = (key) => history[key] === true;
  const metaMap = business?.module_meta && typeof business.module_meta === "object" ? business.module_meta : {};
  const meta = (key) => metaMap[key] || null;                       // {name, classification, entitlement, owner_activatable} or null
  const entitlement = (key) => meta(key)?.entitlement || null;      // standard | optional | premium | custom | industry
  const isOwnerActivatable = (key) => meta(key)?.owner_activatable === true;
  // Why a module is unavailable (UX wording only; backend enforces): needs_activation | included_off | module_off | null (available)
  const unavailableReason = (key) => {
    if (!map || map[key] !== false) return null;
    if (isOwnerActivatable(key)) return "needs_activation";
    return entitlement(key) === "standard" ? "included_off" : "module_off";
  };
  return {
    ready,
    modules: map || {},
    history,
    isEnabled,                                         // legacy `!!business?.features?.key`
    isExplicitlyOff: (key) => !!map && map[key] === false, // legacy `business?.features && features[key] === false` (route guard)
    hasHistory,
    isAvailable: (key) => isEnabled(key) || hasHistory(key), // legacy nav rule `features[key] || module_history[key]`
    meta, entitlement, isOwnerActivatable, unavailableReason,
    moduleName: (key) => meta(key)?.name || key,
  };
}

export function useModules() {
  const { business, loading } = useAuth();
  return useMemo(() => buildModules(business, loading), [business, loading]);
}
